import React, { useState, useEffect } from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useNotification } from "../contexts/NotificationContext";

const FooterNotification: React.FC = () => {
  const { notifications, clearNotifications, removeNotification } =
    useNotification();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(
    null,
  );

  // Reset current index when notifications change
  // Show the newest notification (last in array) when a new one is added
  useEffect(() => {
    if (notifications.length > 0) {
      setCurrentIndex(notifications.length - 1);
    }
  }, [notifications.length]);

  // Adjust current index if it's out of bounds
  useEffect(() => {
    if (currentIndex >= notifications.length && notifications.length > 0) {
      setCurrentIndex(notifications.length - 1);
    }
  }, [currentIndex, notifications.length]);

  // Timer effect for auto-dismiss
  useEffect(() => {
    if (notifications.length === 0) {
      // Clear any existing timer when no notifications
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
      return;
    }

    // Reset timer when notifications change
    if (timerInterval) {
      clearInterval(timerInterval);
    }

    // Start new timer - dismiss after 3.5 seconds
    const interval = setTimeout(() => {
      // Remove notification immediately - no slide animation
      if (notifications.length > 0) {
        const currentNotification =
          notifications[currentIndex] || notifications[0];
        removeNotification(currentNotification.id);
      }
    }, 3500); // 3.5 seconds

    setTimerInterval(interval);

    // Cleanup on unmount or when notifications change
    return () => {
      clearTimeout(interval);
    };
  }, [notifications.length, currentIndex, removeNotification]);

  if (notifications.length === 0) return null;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd || notifications.length <= 1) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentIndex < notifications.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < notifications.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "success":
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case "error":
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case "info":
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getBackgroundColor = (type: string) => {
    switch (type) {
      case "success":
        return "bg-green-50 border-green-200";
      case "error":
        return "bg-red-50 border-red-200";
      case "info":
        return "bg-blue-50 border-blue-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  const currentNotification = notifications[currentIndex] || notifications[0];

  return (
    <div className="fixed bottom-5 left-0 right-0 z-50 px-4">
      <div className="mx-auto max-w-md">
        {/* Main notification container */}
        <div
          className={`
            ${getBackgroundColor(currentNotification.type)}
            border rounded-lg shadow-lg p-3
            relative
          `}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1 min-w-0">
              <button
                onClick={clearNotifications}
                className="flex-shrink-0 mt-0.5 hover:opacity-70 transition-opacity cursor-pointer"
                title="Clear all notifications"
              >
                {getIcon(currentNotification.type)}
              </button>
              <span className="text-sm font-medium text-gray-900 leading-relaxed">
                {currentNotification.message}
              </span>
            </div>

            {/* Clear all button */}
            <button
              onClick={clearNotifications}
              className="text-gray-400 hover:text-gray-600 transition-colors ml-2 flex-shrink-0"
              title="Clear all notifications"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Navigation controls - only show if more than one notification */}
          {notifications.length > 1 && (
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
              {/* Previous button */}
              <button
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                className={`flex items-center space-x-1 text-xs transition-colors ${
                  currentIndex === 0
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <ChevronLeftIcon className="w-3 h-3" />
                <span>Previous</span>
              </button>

              {/* Page indicator */}
              <div className="text-xs text-gray-600 font-medium">
                {currentIndex + 1}/{notifications.length}
              </div>

              {/* Next button */}
              <button
                onClick={goToNext}
                disabled={currentIndex === notifications.length - 1}
                className={`flex items-center space-x-1 text-xs transition-colors ${
                  currentIndex === notifications.length - 1
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <span>Next</span>
                <ChevronRightIcon className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FooterNotification;
